import { useMutation } from '@apollo/client/react';
import {
  Alert,
  Button,
  Form,
  Input,
  Row,
  Select,
  Space,
  message,
} from 'antd';
import { RecaptchaVerifier, signInWithPhoneNumber } from 'firebase/auth';
import parsePhoneNumber from 'libphonenumber-js';
import { filter, map } from 'lodash';
import React, { useEffect, useRef, useState } from 'react';
import countryCodeData from '../../assets/countryCode.json';
import { LoginLogo } from '../../assets/svg/login';
import useLazyQueryWithCallbacks from '../../common/CommonUseLazyQuery';
import {
  COUNTRY_CODES,
  GA_EVENT,
  GA_LABEL,
  LOGIN_TYPE,
  REGEX,
  ROUTES,
} from '../../common/constants';
import { Event } from '../../common/trackEvents';
import useRouter from '../../common/useRouter';
import { getCountryCodeFromLocaleTimeZone } from '../../common/utils';
import auth from '../../firebase';
import { GENERATE_USER_OTP } from './graphql/Mutations';
import { GET_LOGIN_TYPE, IS_USER_EXIST_BY_EMAIL } from './graphql/Queries';

const { Option } = Select;
const Login = () => {
  const [form] = Form.useForm();
  const { navigate, location } = useRouter();
  const [isMobileNumber, setIsMobileNumber] = useState(false);
  const [errorMessage, setErrorMessage] = useState();
  const [isLoading, setIsLoading] = useState(false);
  const pathname = location?.state?.from?.pathname;
  const loginInputRef = useRef(null);

  useEffect(() => {
    try {
      // eslint-disable-next-line no-undef
      window.recaptchaVerifier = new RecaptchaVerifier(
        auth,
        'recaptcha-container',
        {
          size: 'invisible',
        },
      );
    } catch (error) {
      return error;
    }
  }, []);

  useEffect(() => {
    if (location?.state?.countryCode) {
      setIsMobileNumber(true);
    }
    form.setFieldsValue({
      identifier: location?.state?.email || location?.state?.phone,
      countryCode: location?.state?.countryCode || COUNTRY_CODES.IN,
    });
  }, [location?.state]);

  const [generateUserOtp] = useMutation(GENERATE_USER_OTP, {
    onError(error) {
      setErrorMessage(error?.message);
    },
    onCompleted() {
      const { identifier, countryCode } = form.getFieldsValue();
      Event(GA_EVENT.REQUESTED_OTP, {
        label: GA_LABEL.REQUESTED_OTP,
        // eslint-disable-next-line no-undef
        pathname: window?.location?.href,
        identifier,
        countryCode,
      });
    },
  });
  const [isUserExistByEmail, { loading }] = useLazyQueryWithCallbacks(
    IS_USER_EXIST_BY_EMAIL,
    {
      fetchPolicy: 'network-only',
      onError(error) {
        setErrorMessage(error?.message);
      },
    },
  );

  const [getLoginType, { loading: loginTypeLoading }] =
    useLazyQueryWithCallbacks(GET_LOGIN_TYPE, {
      fetchPolicy: 'network-only',
      onError(error) {
        setErrorMessage(error?.message);
        return error;
      },
      onCompleted(res) {
        let identifier = '';
        let countryCode = '';
        const { identifier: identifierValue, countryCode: countryCodeValue } =
          form.getFieldsValue();
        const phoneNumberDetails = parsePhoneNumber(identifierValue || '');
        if (phoneNumberDetails) {
          identifier = phoneNumberDetails?.nationalNumber || identifierValue;
          countryCode = `+${phoneNumberDetails?.countryCallingCode}`;
        } else {
          identifier = identifierValue;
          countryCode = countryCodeValue;
        }
        const countryShortForm = filter(
          countryCodeData,
          (record) => record?.dialCode === countryCode,
        )?.[0]?.countryCode?.toUpperCase();
        if (
          res?.getLoginType?.loginType === LOGIN_TYPE.OTP &&
          countryCode === COUNTRY_CODES.IN
        ) {
          generateUserOtp({
            variables: {
              data: {
                phoneNo: `${countryCode}${identifier}`,
                country: countryShortForm,
              },
            },
          });
          navigate(ROUTES.VERIFY, {
            state: {
              phone: identifier,
              countryCode,
              pathname,
              loginType: res?.getLoginType?.loginType,
              countryShortForm,
            },
          });
        } else if (
          res?.getLoginType?.loginType === LOGIN_TYPE.OTP &&
          countryCode === COUNTRY_CODES.AE
        ) {
          signInWithPhoneNumber(
            auth,
            `${countryCode}${identifier}`,
            // eslint-disable-next-line no-undef
            window.recaptchaVerifier,
          )
            ?.then((confirmationResult) => {
              // eslint-disable-next-line no-undef
              window.confirmationResult = confirmationResult;
              message.success('Otp sent successfully!');
              setIsLoading(false);
              Event(GA_EVENT.REQUESTED_OTP_WITH_FIREBASE, {
                label: GA_LABEL.REQUESTED_OTP_WITH_FIREBASE,
                // eslint-disable-next-line no-undef
                pathname: window?.location?.href,
                phone: identifier,
                countryCode,
              });
              navigate(ROUTES.VERIFY, {
                state: {
                  phone: identifier,
                  countryCode,
                  pathname,
                  loginType: res?.getLoginType?.loginType,
                  countryShortForm,
                },
              });
            })
            ?.catch(() => {
              setIsLoading(false);
              message.destroy();
              message.error('Something went wrong!');
            });
        } else if (res?.getLoginType?.loginType === LOGIN_TYPE.OTP) {
          // eslint-disable-next-line no-undef
          window.sendOtp(
            `${countryCode?.replace('+', '')}${identifier}`,
            () => {
              message.success('Otp sent successfully!');
              setIsLoading(false);
              Event(GA_EVENT.REQUESTED_OTP_WITH_WHATSAPP, {
                label: GA_LABEL.REQUESTED_OTP_WITH_WHATSAPP,
                // eslint-disable-next-line no-undef
                pathname: window?.location?.href,
                phone: identifier,
                countryCode,
              });
              navigate(ROUTES.VERIFY, {
                state: {
                  phone: identifier,
                  countryCode,
                  pathname,
                  loginType: res?.getLoginType?.loginType,
                  countryShortForm,
                },
              });
            },
            () => {
              setIsLoading(false);
              message.destroy();
              message.error('Something went wrong!');
            },
          );
        } else {
          navigate(ROUTES.VERIFY, {
            state: {
              phone: identifier,
              countryCode,
              loginType: res?.getLoginType?.loginType,
              pathname,
              countryShortForm,
            },
          });
        }
      },
    },
  );

  const handleInputChange = (e) => {
    const { value } = e?.target;
    if (value?.length === 0) {
      setIsMobileNumber(false);
    }
    if (REGEX?.NUMBER?.test(value)) {
      setIsMobileNumber(true);
      loginInputRef?.current?.focus();
    } else {
      setIsMobileNumber(false);
    }
    if (REGEX?.EMAIL?.test(value)) {
      setIsMobileNumber(false);
      loginInputRef?.current?.focus();
    }
  };

  useEffect(() => {
    const countryShortForm = getCountryCodeFromLocaleTimeZone();
    const dialCode =
      filter(
        countryCodeData,
        (item) => item?.countryCode === countryShortForm,
      )?.[0]?.dialCode || '+91';
    form.setFieldsValue({
      countryCode: dialCode,
    });
  }, []);

  useEffect(() => {
    loginInputRef?.current?.focus();
  }, [isMobileNumber]);

  const onFinish = async ({
    identifier: identifierValue,
    countryCode: countryCodeValue,
  }) => {
    let identifier = '';
    let countryCode = '';
    const phoneNumberDetails = parsePhoneNumber(identifierValue || '');
    if (phoneNumberDetails && !REGEX?.EMAIL?.test(identifierValue)) {
      identifier = phoneNumberDetails?.nationalNumber || identifierValue;
      countryCode = `+${phoneNumberDetails?.countryCallingCode}`;
    } else {
      identifier = identifierValue;
      countryCode = countryCodeValue;
    }
    if (
      (isMobileNumber || phoneNumberDetails) &&
      !REGEX?.EMAIL?.test(identifierValue)
    ) {
      const countryShortForm = filter(
        countryCodeData,
        (record) => record?.dialCode === countryCode,
      )?.[0]?.countryCode?.toUpperCase();
      try {
        await getLoginType({
          variables: {
            data: {
              phoneNo: `${countryCode}${identifier}`,
              country: countryShortForm,
            },
          },
        });
      } catch (error) {
        return error;
      }
    } else {
      isUserExistByEmail({
        variables: {
          email: identifier,
        },
      }).then((res) => {
        if (res?.data) {
          navigate(ROUTES.VERIFY, {
            state: {
              email: identifier,
              countryCode,
              pathname,
            },
          });
        }
      });
    }
  };

  return (
    <div className="gx-login-container">
      <div id="recaptcha-container" className="visibility-hidden" />
      <div id="login-logo" className="login-logo">
        <LoginLogo width="200" height="60" />
      </div>
      {errorMessage && (
        <Alert
          title={<h3>There was a problem</h3>}
          description={errorMessage}
          type="error"
          showIcon
        />
      )}
      <div className="gx-login-content">
        <div className="card-body">
          <h1>Sign in</h1>
          <Form
            layout="vertical"
            name="Login"
            className="gx-login-form gx-form-row0 login-form"
            form={form}
            initialValues={{ countryCode: COUNTRY_CODES.IN }}
            onFinish={onFinish}
          >
            <Form.Item
              label="Enter mobile number or email"
              className="phone-input"
            >
              <Space.Compact className="width-percent-100">
                {isMobileNumber && (
                  <Form.Item name="countryCode" noStyle>
                    <Select
                      id="country-code-select"
                      showSearch
                      className="country-code-input"
                    >
                      {map(countryCodeData, (record) => (
                        <Option key={record?.id} value={record?.dialCode}>
                          {record?.dialCode}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                )}
                <Form.Item
                  name="identifier"
                  noStyle
                  rules={[
                    () => ({
                      validator(_, value) {
                        if (!value) {
                          return Promise.reject(new Error('Required'));
                        }
                        if (REGEX.EMAIL?.test(value)) {
                          return Promise.resolve();
                        }
                        if (REGEX.PHONE?.test(value)) {
                          return Promise.resolve();
                        }
                        return Promise.reject(
                          new Error('Please enter a valid email or phone number'),
                        );
                      },
                    }),
                  ]}
                  label="Enter mobile number or email"
                  className="phone-input"
                >
                  <Input
                    id="login-identifier-input"
                    ref={loginInputRef}
                    onChange={handleInputChange}
                    allowClear
                    className={
                      isMobileNumber
                        ? 'country-code-phone-input'
                        : ''
                    }
                    onKeyDown={(e) => {
                      if (e?.code === 'Space') {
                        e?.preventDefault();
                      }
                    }}
                  />
                </Form.Item>
              </Space.Compact>
            </Form.Item>
            <Row className="login-row">
              <Form.Item className="login-button">
                <Button
                  id="login-continue-button"
                  type="primary"
                  htmlType="submit"
                  shape="round"
                  loading={loginTypeLoading || isLoading || loading}
                >
                  Continue
                </Button>
              </Form.Item>
            </Row>
          </Form>
        </div>
      </div>
    </div>
  );
};

export default Login;
